/**
 * evalMarkups — the eval-canvas MARKUP model + pure ops (mockup v3.5 / the PKA
 * AVBP_MAP_MARKUP_SYSTEM spec). A markup is an ANNOTATION drawn over the plan —
 * separate from the numbered door/AUO disposition. The locked first set:
 *   tick ✓ · cross ✗ · question ? · revision-cloud ☁ (scalable scalloped rect)
 *   · flip/mirror ▥ (scalable rect + axis) · arrow → · text T.
 *
 * Geometry in IMAGE-PIXEL space (OQ-C — the same space the boundary/grid/markers
 * live in), so markups pan/zoom with the plan and align to it. Point glyphs carry
 * a centre {x,y}; rect shapes (cloud / mirror) carry {x,y,w,h}; the arrow carries
 * a second point {x2,y2}.
 *
 * PURE — no DOM, no Firebase. (This pass keeps markups in component state; a
 * persist patch can ride alongside doors/fixtures later, like entranceMarks.)
 */

import { zoneAtPoint, type GridResult, type Pt } from './vastuGrid';

export type MarkupType =
  | 'tick'
  | 'cross'
  | 'question'
  | 'cloud'
  | 'mirror'
  | 'arrow'
  | 'text';

export const MARKUP_TYPES: readonly MarkupType[] = [
  'tick', 'cross', 'question', 'cloud', 'mirror', 'arrow', 'text',
] as const;

/** The SINGLE source of truth for the staff-facing markup tool labels
 *  (owner-confirmed canonical wording). Both surfaces (this file's consumers +
 *  EvalDispositionList) import from here — no duplicate map. */
export const MARKUP_LABEL: Record<MarkupType, string> = {
  tick: 'OK / approved',
  cross: 'Not permissible',
  question: 'Remedy required',
  cloud: 'Revision cloud',
  mirror: 'Mirror region',
  arrow: 'Arrow',
  text: 'Text call-out',
};

export interface EvalMarkup {
  id: string;
  type: MarkupType;
  /** centre (point glyphs) OR top-left (rect shapes) OR start (arrow), image px. */
  x: number;
  y: number;
  /** rect shapes (cloud / mirror) — width/height in image px. */
  w?: number;
  h?: number;
  /** arrow — the end point. */
  x2?: number;
  y2?: number;
  /** optional free note (shown in the Markups disposition row). */
  note?: string;
  /** optional staff-set custom label (overrides the type's default name in the
   *  Markups disposition row). Blank/absent → the type label is used. */
  label?: string;
}

export const isRectMarkup = (t: MarkupType): boolean => t === 'cloud' || t === 'mirror';

let SEQ = 0;
function newId(): string {
  SEQ += 1;
  return `mk${Date.now().toString(36)}${SEQ}`;
}

/** PURE — build a markup at a click point, sized in IMAGE px from the plan size
 *  so defaults read sensibly at any plan scale. */
export function makeMarkup(type: MarkupType, click: { x: number; y: number }, planSize: number): EvalMarkup {
  const x = Math.round(click.x);
  const y = Math.round(click.y);
  const unit = Math.max(40, Math.round(planSize / 8)); // ~plan-relative default
  if (type === 'cloud' || type === 'mirror') {
    return { id: newId(), type, x: x - unit, y: y - Math.round(unit * 0.66), w: unit * 2, h: Math.round(unit * 1.3) };
  }
  if (type === 'arrow') {
    return { id: newId(), type, x, y, x2: x + unit, y2: y + Math.round(unit * 0.6) };
  }
  return { id: newId(), type, x, y };
}

/** PURE — translate a markup so its anchor follows the drag point. */
export function moveMarkup(list: EvalMarkup[], id: string, drag: { x: number; y: number }): EvalMarkup[] {
  return list.map((m) => {
    if (m.id !== id) return m;
    const nx = Math.round(drag.x);
    const ny = Math.round(drag.y);
    if (m.type === 'arrow' && m.x2 != null && m.y2 != null) {
      const dx = nx - m.x; const dy = ny - m.y;
      return { ...m, x: nx, y: ny, x2: m.x2 + dx, y2: m.y2 + dy };
    }
    return { ...m, x: nx, y: ny };
  });
}

/** PURE — resize a rect markup (cloud / mirror) by dragging its bottom-right grip
 *  to `drag`; or move an arrow's END point. Min size keeps it grabbable. */
export function resizeMarkup(list: EvalMarkup[], id: string, drag: { x: number; y: number }): EvalMarkup[] {
  return list.map((m) => {
    if (m.id !== id) return m;
    if (m.type === 'arrow') return { ...m, x2: Math.round(drag.x), y2: Math.round(drag.y) };
    if (m.w == null || m.h == null) return m;
    const w = Math.max(20, Math.round(drag.x) - m.x);
    const h = Math.max(16, Math.round(drag.y) - m.y);
    return { ...m, w, h };
  });
}

/** PURE — set a markup's free note (the inline-edit affordance). Trims; an empty
 *  string clears the note so the row falls back to just the label. Matched by id. */
export function setMarkupNote(list: EvalMarkup[], id: string, note: string): EvalMarkup[] {
  const next = (note || '').trim();
  return list.map((m) => (m.id === id ? { ...m, note: next || undefined } : m));
}

/** PURE — set a markup's custom label (the inline-edit affordance). Trims; an
 *  empty string clears it so the row falls back to the type's default name.
 *  Matched by id. */
export function setMarkupLabel(list: EvalMarkup[], id: string, label: string): EvalMarkup[] {
  const next = (label || '').trim();
  return list.map((m) => (m.id === id ? { ...m, label: next || undefined } : m));
}

/** PURE — remove a markup by id. */
export function removeMarkup(list: EvalMarkup[], id: string): EvalMarkup[] {
  return list.filter((m) => m.id !== id);
}

/** PURE — append a markup. */
export function addMarkup(list: EvalMarkup[], m: EvalMarkup): EvalMarkup[] {
  return [...list, m];
}

/** PURE — the SET of 16-zones a markup's geometry falls over, DERIVED off the grid
 *  (the same `zoneAtPoint` classifier doors + AUOs use, in the shared image-px OQ-C
 *  space). A markup has NO disposition, but it DOES cover a zone set — the workspace
 *  expand-detail shows it. We sample the markup's anchor plus, for rects and arrows,
 *  their spanning corners/endpoint, and union the zones each sample resolves to.
 *  Returns [] when there is no grid or the markup is fully off-grid. */
export function zonesForMarkup(grid: GridResult | null, m: EvalMarkup): string[] {
  if (!grid) return [];
  const pts: Pt[] = [];
  if (isRectMarkup(m.type) && m.w != null && m.h != null) {
    // sample the rect's centre + four corners so a large cloud reads every zone.
    const cx = m.x + m.w / 2;
    const cy = m.y + m.h / 2;
    pts.push(
      { x: cx, y: cy },
      { x: m.x, y: m.y },
      { x: m.x + m.w, y: m.y },
      { x: m.x, y: m.y + m.h },
      { x: m.x + m.w, y: m.y + m.h },
    );
  } else if (m.type === 'arrow' && m.x2 != null && m.y2 != null) {
    // both endpoints + the midpoint of the arrow.
    pts.push(
      { x: m.x, y: m.y },
      { x: m.x2, y: m.y2 },
      { x: (m.x + m.x2) / 2, y: (m.y + m.y2) / 2 },
    );
  } else {
    pts.push({ x: m.x, y: m.y });
  }
  const seen: string[] = [];
  for (const p of pts) {
    const z = zoneAtPoint(grid, p);
    if (z && !seen.includes(z)) seen.push(z);
  }
  return seen;
}
